import React, { useCallback, useState } from 'react';

import ObjectiveLevelSelectionComp from 'components/ObjectiveTagList/ObjectiveLevelSelectionComp';
import ObjectiveNameAutoComplete from 'components/ObjectiveTagList/ObjectiveNameAutoComplete';
import { TagDescription } from 'components/ObjectiveTagList/TagDescription';
import OrgObjective from 'models/orgObjective';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { FlexContainer, StyledBox, StyledIconButton } from 'styles/common';

import { OBJECTIVE_LEVEL_TAGS } from 'constants/ProjectConstants';
import { CrossIcon, CheckIcon2, CloseIcon2 } from 'constants/icons';
import { sendCreatedObjectiveEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { saveObjective } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography6 } from 'uiToolkit/Typography';

export default function TreeViewObjectiveCreationModalView({
  onCreate,
  onCancel,
  selectedTimeBoundPeriod,
  parentObjective = null,
}) {
  const dispatch = useDispatch();

  const addTag = (tag) => {
    // following fields will be sent to BE.
    if (!tag?.name) {
      dispatch(showErrorSnackbar('Please enter objective name!'));
      return;
    }
    if (!tag?.objectiveLevel) {
      dispatch(showErrorSnackbar('Please select objective level!'));
      return;
    }
    saveObjective({ tag })
      .then((savedTag) => {
        onCreate(savedTag);
        sendCreatedObjectiveEvent({
          eventProperties: {
            source: 'Tree View',
            timePeriod: tag.timeBoundDetails.timelineTag,
            level: tag.objectiveLevel,
            description: tag.description,
            objectiveId: savedTag.id,
            type: tag?.parentTag?.id ? 'Sub-objective' : 'Objective',
          },
        });
        onCancel();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      });
  };
  return (
    <Wrapper>
      <StyledHeader closeModal={onCancel} />
      <CreateNewEditableTagRow
        key="create-new-row"
        onCancel={onCancel}
        onSave={addTag}
        isEditing
        selectedTimeBoundPeriod={selectedTimeBoundPeriod}
        parentObjective={parentObjective}
      />
    </Wrapper>
  );
}

const Header = ({ className, closeModal }) => (
  <div className={className}>
    <Typography6 weight={400}>Create new objective</Typography6>
    {/* <Typography4 color={theme.palette.text.dark1} topGutter={1}>
        Select or create an objective for your goal
      </Typography4> */}

    <CrossIcon
      size="fixed16px"
      style={{ position: 'absolute', top: '1.5rem', right: '1.5rem' }}
      onClick={closeModal}
      color={theme.palette.icons.mediumGrey}
    />
  </div>
);

const CreateNewEditableTagRow = ({
  onSave,
  onCancel,
  isEditing = true,
  selectedTimeBoundPeriod,
  parentObjective = null,
  modalView = true,
  enableCreation = true,
}) => {
  const [currentTag, setCurrentTag] = useState({
    ...new OrgObjective({}),
    timeBoundDetails: selectedTimeBoundPeriod,
    parentTag: !parentObjective ? null : { id: parentObjective.id, uuid: parentObjective.id },
    isEditing: true,
  });

  const { name, description, objectiveLevel } = currentTag;

  const handleTagChange = useCallback((value, objectiveData = null, isNew = false) => {
    if (isNew) {
      setCurrentTag((oldTag) => ({
        ...oldTag,
        name: value,
        isNew: true,
      }));
    } else {
      setCurrentTag((oldTag) => ({
        ...oldTag,
        name: value,
        ...(objectiveData ? { objectiveId: objectiveData.objectiveId } : {}),
      }));
    }
  }, []);

  const handleOnChange = useCallback(({ target: { name, value } }) => {
    setCurrentTag((oldTag) => ({ ...oldTag, [name]: value }));
  }, []);

  const handleTagSubTypeChange = useCallback(
    (tagData) => {
      // following is a hacky way of closing Custom Menu dropdown on selecting one item
      // dummyRef.current.click();
      setCurrentTag((oldTag) => ({ ...oldTag, objectiveLevel: tagData.value }));
    },
    [setCurrentTag]
  );

  return (
    <RowWrapper isEditing={isEditing}>
      <div paddingtop="0" paddingBottom="0" paddingLeft="0">
        <ObjectiveLevelSelectionComp
          objectiveLevel={objectiveLevel}
          onSelect={handleTagSubTypeChange}
          isEditing={isEditing}
          options={OBJECTIVE_LEVEL_TAGS}
        />
      </div>
      <div style={{ flexGrow: 1, marginLeft: '16px' }}>
        <TextFieldContainer isEditing={isEditing}>
          <ObjectiveNameAutoComplete
            //   disabled={!isEditing}
            onInputChange={handleTagChange}
            inputValue={name}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
          />
        </TextFieldContainer>
      </div>
      <TagDescription
        isEditing={isEditing}
        description={description}
        // handleKeyDown={handleKeyDown}
        handleOnChange={handleOnChange}
      />
      <FlexContainer style={{ justifyContent: 'flex-end' }}>
        <FullWidthBox flexcontainer justifycontent="flex-end" alignitems="center">
          <StyledIconButton rightGutter={2} onClick={() => onSave(currentTag)}>
            <CheckIcon2 circularOutline containerSize="fixed24px" size="fixed10px" color="green" bgColor="white" />
          </StyledIconButton>
          <StyledIconButton onClick={onCancel}>
            <CloseIcon2 circularOutline containerSize="fixed24px" size="fixed10px" color="red" bgColor="white" />
          </StyledIconButton>
        </FullWidthBox>
      </FlexContainer>
    </RowWrapper>
  );
};

const Wrapper = styled.div`
  ${
    '' /* height: 100vh;
  width: 100%; */
  }
  max-width: 720px;
`;

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  height: 40px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-left: 0px;


  
  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${props.theme.palette.background.paper};
      border: 1px solid ${props.theme.palette.borders.lighterGrey};
      border-radius: 5px;
    `}
    input {
      padding-left 12px;
    }
`;

const ModalContainer = styled.div`
  width: 50%;
  max-height: 60%;
  min-height: 40%;

  border-radius: 4px;

  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
`;

const StyledHeader = styled(Header)`
  position: relative;
  padding: 1.5rem;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const FullWidthBox = styled(StyledBox)`
  width: 100%;
`;

const RowWrapper = styled.div`
  height: 62px;
  display: flex;
  margin-top: 32px;
  margin-bottom: 32px;
  padding: 0 24px;
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  justify-content: space-between;
  align-items: center;
  ${(props) =>
    props.isEditing &&
    css`
      background-color: #e9f6ff;
    `}
`;
